import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Modal, { Field, TextInput, TextArea, PrimaryButton, SecondaryButton } from "@/components/FormKit";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const INITIAL = {
  company_name: "",
  location: "",
  contact_person: "",
  email: "",
  phone: "",
  message: "",
};

export default function DemoRequestModal({ open, onClose }) {
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setForm(INITIAL);
    setDone(false);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/demo-requests", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={done ? "Request received" : "Request a product demo"}
      testId="demo-request-modal"
      maxWidth="max-w-xl"
    >
      {done ? (
        <div data-testid="demo-request-success">
          <div className="flex items-center gap-3 border-l-4 border-green-600 bg-green-50 p-4">
            <CheckCircle2 size={22} className="text-green-700 shrink-0" />
            <div>
              <div className="font-display font-bold text-stone-950">Thanks for reaching out.</div>
              <p className="text-sm text-stone-700 mt-1">
                Our team at SavvySpark Solutions will contact you within one business day
                to schedule your walkthrough of Savvy.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <PrimaryButton type="button" onClick={close} testId="demo-request-close">
              Done
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <form onSubmit={submit}>
          <p className="text-sm text-stone-600 mb-4">
            Tell us a little about your company and we'll schedule a personalised walkthrough.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Company name">
              <TextInput
                testId="demo-form-company"
                required
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Acme Builders Pvt Ltd"
              />
            </Field>
            <Field label="Location">
              <TextInput
                testId="demo-form-location"
                required
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="City, State"
              />
            </Field>
            <Field label="Contact person">
              <TextInput
                testId="demo-form-contact"
                required
                value={form.contact_person}
                onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                placeholder="Full name"
              />
            </Field>
            <Field label="Email">
              <TextInput
                testId="demo-form-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Phone">
                <TextInput
                  testId="demo-form-phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 98xxxx xxxx"
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="What would you like to see? (optional)">
                <TextArea
                  testId="demo-form-message"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Which portals, modules, or integrations are most important to you?"
                />
              </Field>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={close}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={busy} testId="demo-form-submit">
              {busy ? "Sending…" : "Request demo"}
            </PrimaryButton>
          </div>
        </form>
      )}
    </Modal>
  );
}
